import { createZoomImageWheel } from '@zoom-image/core'
import { VisiblityElement } from '../visibility/visibility'

/**
 * LightboxGallery - CUSTOM ELEMENT
 * Extended off VisiblityElement.
 *
 * View the lighthouse.liquid file to see how element is setup.
 *
 * 1. Ensure you wrap your modal elements with <x-lighthouse-gallery>.
 * 2. Ensure your x-lighthouse-gallery has the data-in-view set to the index of the element you are in view.
 * 3. Ensure your elements that will act as the 'trigger' have the date role data-role="lighthouse-trigger" set.
 *
 */
class LightBoxElement extends VisiblityElement {
  constructor() {
    super()
    // selectors
    this.selectors = {
      zoomContainer: 'div[data-role="zoom_image_container"]',
      container: 'div[data-role="gallery_image_container"]',
      buttons: 'button[data-role="gallery-button"]',
      globalTriggers: 'div[data-role="lightbox-trigger"]',
      close: '[data-role="close"]',
      scrollValue: '[data-role="scroll-value"]',
      thumbnails: '[data-role="lightbox-trigger-zoom"]',
      mobileContainer: '[data-role="base-container"]',
      miniGallery: '[data-role="mini-gallery"]',
      thumbnailSelected: '[data-role="mini-gallery-selected"]'
    }

    // scroll
    this.scrollBehavior = {
      default: { behavior: 'smooth', inline: 'start' },
      none: {}
    }

    this.galleryActions = {
      next: 'next',
      prev: 'prev'
    }

    this.handleInView = (index) => {
      this.dataset.inView = index
    }

    // global variables
    this.zoomContainerReferences = []
    this.galleryImages = this.querySelectorAll(this.selectors.container)

    /** Init */
    this.handleStateChange()
  }

  /**
   *
   * @param {*} payload
   * @returns
   */
  handleStateChange(payload) {
    /** Get the current index and the images */
    const index = payload?.index ?? this.dataset.inView
    const images = this.galleryImages
    const parsedIndex = parseInt(index)

    /** Reset zoom on index mutation. */
    if (this.zoomContainerReferences.length) {
      this.zoomContainerReferences[parsedIndex].setState({ currentZoom: 0 })
    }

    /** Update index state based on button action data-action  */
    const newIndex = this.galleryActionsMap(payload?.action, parsedIndex, images.length)

    /** Update index */
    this.handleInView(newIndex.toString())

    /** Scroll to next image */
    images[newIndex].scrollIntoView(this.scrollBehavior[payload?.scrollBehavior])
    this.handleZoomValue(newIndex, images)

    return newIndex
  }

  /**
   *
   * Updates the zoom value and displays for mobile users.
   *
   * @param {*} index
   * @param {*} images
   */
  handleZoomValue(index, images) {
    const currentZoom = images[index].querySelector(this.selectors.zoomContainer).dataset.mobileZoom
    const scrollElement = document.querySelector(this.selectors.scrollValue)
    scrollElement.innerHTML = `(${currentZoom}%)`
  }

  /**
   *
   * @param {*} currentIndex
   * @param {*} images
   */
  handleGalleryWrap(currentIndex, images) {
    const wrap = images[currentIndex].dataset.type
    if (wrap) {
      const resetIndex = currentIndex >= images.length - 1 ? 1 : images.length - 2
      setTimeout(() => {
        images[resetIndex].scrollIntoView({})
        this.handleInView(resetIndex.toString())
        this.handleZoomValue(resetIndex, images)
      }, 1000)
    }
  }

  /**
   * Handles the gallery buttons
   * @param {*} e
   */
  handleButtons(e) {
    const button = e.target
    const action = button.dataset.action
    const images = this.galleryImages
    const newIndex = this.handleStateChange({ action, scrollBehavior: 'default', images })
    this.handleGalleryWrap(newIndex, images)
  }

  /**
   * Opens the modal and displays the image selected
   * @param {*} e
   */
  handleGlobalTrigger(e) {
    this.handleToggle()
    const image = e.currentTarget
    const index = image.dataset.index
    this.handleStateChange({ index, scrollBehavior: 'none' })
  }

  /** Close the lighthouse */
  handleClose() {
    this.handleHide()
  }

  thumbnailsSelected(el, input) {
    const bar = document.querySelector(this.selectors.thumbnailSelected)
    if (!bar) return
    bar.style.left = `${input}px`

    if (el) {
      bar.style.width = `${el.clientWidth}px`
    }
  }

  calculatePercentage(part, whole) {
    return (part / whole) * 100
  }

  calculateValueFromPercentage(whole, percentage) {
    return (whole * percentage) / 100
  }

  handleContainerProgressBar(e) {
    const ele = e.currentTarget
    const miniGalleryContainer = document.querySelector(this.selectors.miniGallery)
    const percent = this.calculatePercentage(ele.scrollLeft, ele.scrollWidth)
    const width = miniGalleryContainer.scrollWidth
    const input = this.calculateValueFromPercentage(width, percent)
    this.thumbnailsSelected(null, input)
  }

  handleThumbNailScroll() {
    const container = document.querySelector(this.selectors.mobileContainer)
    container.addEventListener('scroll', this.handleContainerProgressBar.bind(this))
    const images = container?.children
    const thumbnails = document.querySelectorAll(this.selectors.thumbnails)
    this.thumbnailsSelected(thumbnails[0], 0)
    thumbnails.forEach((el) => {
      const index = parseInt(el.dataset.index) - 1
      el.addEventListener('click', (e) => {
        // Track-only scroll: scrollIntoView also scrolls ancestors, jumping the page.
        container.scrollTo({ left: images[index].offsetLeft, behavior: 'smooth' })
        const input = parseInt(el.clientWidth) * index
        this.thumbnailsSelected(el, input)
      })
    })
  }

  /**
   * Applies zoom functionality to zoom [data-role="zoom_image_container"]
   */
  handleImageZoom() {
    this.querySelectorAll(this.selectors.zoomContainer).forEach((el) => {
      const result = createZoomImageWheel(el)
      /** Store zoom context in global variable */
      this.zoomContainerReferences.push(result)
      result.subscribe(({ state }) => {
        this.imageZoomUpdate(state, el)
      })
    })
  }

  /**
   * Callback function to handle zoom update for selected image
   */
  imageZoomUpdate(state, element) {
    try {
      const scroll = Math.round(state?.currentZoom * 100)
      const scrollElement = document.querySelector(this.selectors.scrollValue)
      element.dataset.mobileZoom = scroll
      scrollElement.innerHTML = `(${scroll}%)`
    } catch (err) {
      console.error(err)
    }
  }

  /**
   *
   * Reducer to handle the gallery index actions
   *
   * @param {*} action
   * @param {*} currentValue
   * @param {*} totalImages
   * @returns
   */
  galleryActionsMap(action, currentValue, totalImages) {
    switch (action) {
      case this.galleryActions.next:
        if (currentValue < totalImages) {
          return currentValue + 1
        }
        break
      case this.galleryActions.prev:
        if (currentValue > 0) {
          return currentValue - 1
        }
        break
      default:
        return currentValue
    }
  }

  /**
   * TODO change function name to addEventListeners once we figure out
   * how to get both the parent and child functions to execute together.
   */
  /** Add event listeners */
  addEventListeners() {
    super.addEventListeners()

    this.querySelectorAll(this.selectors.buttons).forEach((el) => {
      el.addEventListener('click', this.handleButtons.bind(this))
    })

    document.querySelectorAll(this.selectors.globalTriggers).forEach((el) => {
      el.addEventListener('click', this.handleGlobalTrigger.bind(this))
    })

    document.querySelectorAll(this.selectors.close).forEach((el) => {
      el.addEventListener('click', this.handleClose.bind(this))
    })
  }

  /** Remove event listeners */
  /**
   * TODO change function name to removeEventListeners once we figure out
   * how to get both the parent and child functions to execute together.
   */
  removeEventListeners() {
    super.removeEventListeners()

    this.querySelectorAll(this.selectors.buttons).forEach((el) => {
      el.removeEventListener('click', this.handleButtons.bind(this))
    })

    document.querySelectorAll(this.selectors.globalTriggers).forEach((el) => {
      el.removeEventListener('click', this.handleGlobalTrigger.bind(this))
    })

    document.querySelectorAll(this.selectors.close).forEach((el) => {
      el.removeEventListener('click', this.handleClose.bind(this))
    })
  }

  connectedCallback() {
    this.handleImageZoom()
    this.addEventListeners()
    this.handleThumbNailScroll()
  }

  disconnectedCallback() {
    this.removeEventListeners()
  }
}

customElements.define('x-lightbox', LightBoxElement)
